import { AuthService } from '@/services';
import { MealCountKey } from '@/models';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import Vue from 'vue'
import { inject, injectable } from 'inversify-props'
import { eventBus, mcLogger, mcEvents, mcStatus } from '@/utils'
import { MergedLines } from '@/models/merged-lines';

export interface ICountHub {
  getConnection(): Promise<HubConnection>;
  eventBus: Vue; // Use eventBus for emitting things to other Vue components

  /**
   * Notify SignalR that we want to join a group
   * @param groupId The group we want to join
   */
  joinGroupAsync(groupId: string): Promise<any> | void;

  /**
   * Notify SignalR that we want to leave a group
   *
   * @param groupId The group we want to leave
   * @returns Nothing if there isn't an active connection, a Promise otherwise
   */
  leaveGroupAsync(groupId: string): Promise<any> | void;
}

@injectable()
export class CountHub implements ICountHub {
  @inject() private authService!: AuthService;
  private console: Console = mcLogger.getConsole(this.constructor.name, mcLogger.colors.CountHub);
  //private startedPromise!: Promise<any> | null;
  private manuallyClosed: boolean = false;

  private _connection!: HubConnection;
  countHubAddress: string = "/count-hub";
  eventBus: Vue = eventBus;

  /** Matching names for ICountHub and CountHub method names */
  signalRBroadcasts = {
    joinCountGroup: 'JoinCountGroup',
    leaveCountGroup: 'LeaveCountGroup',
    receiveUpdatedCount: 'ReceiveUpdatedCount',
    mergedLines: 'mergedLines'
  };


  constructor() {
  }

  /**
   * Get a HubConnection to CountHub
   * If a connection is not already created, create one now and start it
   * @returns A HubConnection to CountHub
   */
  async getConnection(): Promise<HubConnection> {
    if (!this._connection) {
      this.console.info(`Connection to ${this.countHubAddress} does not exist, creating one now.`);
      const accessToken = await this.authService.getAccessToken() ?? "";
      this._connection = new HubConnectionBuilder()
        .configureLogging(mcStatus.isInDevMode() ? LogLevel.Debug : LogLevel.Warning)
        .withUrl(this.countHubAddress, { accessTokenFactory: () =>  accessToken })
        .withAutomaticReconnect()
        .build();
    }

    return this._connection;
  }

  private async startSignalRAsync() {
    //TODO: expand check for connecting
    const state = this._connection.state;
    if (state !== HubConnectionState.Connected
      && state === HubConnectionState.Disconnected) {
      await this._connection.start()
        .then(() => {
          this.setupBroadcastListeners();
        })
        .catch(err => {
          this.console.error('Failed to connect with hub because of an error:', err);
        });
    }
  }

  /**
   * Setup broadcast listeners and their emit responses
   * @remark Be sure to keep this in sync with disableBroadcastListeners()
   */
  private async setupBroadcastListeners() {
    let connection = await this.getConnection();
    connection.on(this.signalRBroadcasts.receiveUpdatedCount, (mealCountKey: MealCountKey) => {
      this.eventBus.$emit(mcEvents.receiveUpdatedCount, mealCountKey);
    });
    connection.on(this.signalRBroadcasts.mergedLines, (mergedLines: MergedLines) => {
      this.eventBus.$emit(mcEvents.mergedLines, mergedLines);
    });
  }

  /**
   * @remark Be sure to keep this in sync with setupBroadcastListeners()
   */
  private async tearDownBroadcastListeners() {
    let connection = await this.getConnection();
    connection.off(this.signalRBroadcasts.receiveUpdatedCount);
  }

  /**
   * Stop the SignalR connection
   */
  async stopSignalRAsync() {
    this.manuallyClosed = true;
    let connection = await this.getConnection();
    if (connection.state === HubConnectionState.Connected) {
      connection.stop().then(() => {
        this.tearDownBroadcastListeners();
      }).catch(err => {
        this.console.error('Failed to stop connection', err);
      });
    }
  }

  async joinGroupAsync(groupId: string): Promise<any> {
    this.console.info('Joining group:', groupId);

    // Links to CountHub.JoinCountGroup(string groupId)
    let connection = await this.getConnection();
    await this.startSignalRAsync();

    connection
      .invoke(this.signalRBroadcasts.joinCountGroup, groupId)
      .catch((err) => {
        this.console.error('Unable to join the group because of an error:', err);
      });
  }

  async leaveGroupAsync(groupId: string): Promise<any> {
    this.console.info('Leaving group:', groupId);

    // Links to CountHub.JoinCountGroup(string groupId)
    let connection = await this.getConnection();

    if (connection.state === HubConnectionState.Connected) {
      connection
        .invoke(this.signalRBroadcasts.leaveCountGroup, groupId)
        .then(async () => {
          await this.stopSignalRAsync();
        })
        .catch((err) => {
          this.console.error('Unable to leave the group because of an error:', err);
        });
    }
  }
}

export const countHub = new CountHub();
